/**
 * Morse „Augen zu“ — EINE Aufnahme zuhören, bis ein Buchstabe gesagt ist. (13.10.2026)
 *
 * Der Knopf „Sprechen“ (morse-sprechen.ts) wartet auf einen Tipp und lässt Zeit zum Prüfen; hier tippt niemand. Der
 * `Zuhoerer` öffnet das Mikrofon über denselben Anschluss (`DiktatAnschluss` → LiveDictation, KEIN zweiter Aufnahme-Motor),
 * hört, bis etwas gesagt UND wieder still ist (oder nichts kommt), und gibt EIN Ergebnis zurück. Kein Audio wird
 * aufgehoben; die Mitschrift lebt nur im Speicher.
 *
 * Zeiten (alles in `AUGEN_…`/morse-sprechen.ts, hier nur gelesen):
 *   - Die Frist „nichts gehört“ beginnt, wenn das Mikrofon STEHT (`bereit`) — mit AirPods vergehen vorher 1,5–4,5 s
 *     (Wechsel A2DP→HFP), die ihm nicht von der Antwortzeit abgehen dürfen. `mikroMs` ist genau diese Zeit (Messzeile).
 *   - Nach dem Gesagten endet es nach `AUGEN_STILLE_MS` Stille, nach einem Satzende der Erkennung schon nach `SATZENDE_MS`.
 *   - Steht das Mikrofon nach `MIKRO_FRIST_MS` nicht, ist es ein Fehler (der Übung bleibt ein Satz in Worten).
 *   - Sagt `frueh` (die Übung), das Gehörte sei schon ein klarer Buchstabe, endet es nach `AUGEN_FRUEH_STILLE_MS` und wartet
 *     auf den Abschluss der Erkennung nur `AUGEN_FRUEH_ABSCHLUSS_MS`. (15.10.2026)
 *   - Ist der klare Buchstabe schon BESTÄTIGT (`fest`, nichts Vorläufiges mehr), endet es nach `AUGEN_FEST_STILLE_MS` und
 *     wartet gar nicht auf den Abschluss — er kann Bestätigtes nicht mehr ändern. (23.10.2026, „fast 1 Sekunde bis Feedback“)
 *   - Länger als `AUGEN_HOECHSTENS_MS` nimmt es nie auf.
 *   - Die Stille-Uhr wird nur von NEUEM Text neu aufgezogen: die Erkennung meldet dasselbe Wort mehrmals (vorläufig,
 *     bestätigt, mit Punkt) — jede Meldung schob das Ende bis dahin um weitere 850 ms hinaus. (15.10.2026)
 */
import { AUGEN_FEST_STILLE_MS, AUGEN_FRUEH_ABSCHLUSS_MS, AUGEN_FRUEH_STILLE_MS, AUGEN_HOECHSTENS_MS, AUGEN_STILLE_MS, AUGEN_WARTE_MS } from "./morse-augen-logik.js";
import {
  DiktatFehler,
  SATZENDE_MS,
  type DiktatAnschluss,
  type DiktatFehlerArt,
  type DiktatLauf,
} from "./morse-sprechen.js";
import type { SprachWort } from "./morse-sprechen-leser.js";

/** So lange darf das Öffnen des Mikrofons dauern (AirPods bis ~4,5 s), bevor es ein Fehler ist. */
export const MIKRO_FRIST_MS = 10_000;

type ZuhoerGrund = "stille" | "satzende" | "hoechstens" | "kein-text" | "abbruch" | "fehler";

export interface ZuhoerErgebnis {
  grund: ZuhoerGrund;
  /** Was die Erkennung schrieb (leer bei Stille, Abbruch und Fehler). */
  text: string;
  woerter?: SprachWort[];
  /** Von `hoere()` bis das Mikrofon stand (ms) — `null`, wenn es nie stand. */
  mikroMs: number | null;
  /** Nur bei `fehler`. */
  fehler?: DiktatFehlerArt;
  /** Vom letzten NEUEN Text bis das Mikrofon zuging (ms) — `null` ohne Text. Nur Zahlen, für die Messzeile. */
  stilleMs?: number | null;
  /** Vom Zugehen des Mikrofons bis die Mitschrift fertig war (ms). */
  abschlussMs?: number | null;
  /** Endete es früh, weil schon ein klarer Buchstabe gehört war? */
  frueh?: boolean;
  /**
   * Die Erkennung hat unterwegs etwas geschrieben und am Ende wieder zurückgezogen — `text`/`woerter` sind dann das zuletzt
   * Gehörte. (21.10.2026, Journal 15:10:50 bei soll=U: `frueh=1` — ein klarer Buchstabe war da —, aber `erkannt=0`.) Ein
   * einzelnes „U“ schreibt die Erkennung gern englisch „You.“, und das Live-Diktat verwirft „you“ am Ende als Stille-Floskel
   * (shared/diktat-floskeln.ts). Die Übung wertet so etwas nie, sie bietet es nur zum Antippen an (`waehleKandidat`, vorsichtig).
   */
  nachgeholt?: boolean;
}

export interface HoerOptionen {
  /** Ist das Gehörte schon eine klare Antwort? Dann endet die Aufnahme nach kurzer Stille. Wirft nie (sonst: nein). */
  frueh?: (text: string, woerter?: readonly SprachWort[]) => boolean;
  /**
   * Darf ein bestätigter klarer Buchstabe OHNE Abschluss gelten (`AUGEN_FEST_STILLE_MS`)? Nein, wenn das Gehörte noch wachsen
   * kann: ein bestätigtes „Em“ ist auch der Anfang von „Emil“ (E) — dann der Weg mit Abschluss. Fehlt es: ja. Wirft nie (sonst: nein).
   */
  sofort?: (text: string, woerter?: readonly SprachWort[]) => boolean;
}

/** Text ohne Satzzeichen und Groß/klein: „K.“ und „k“ sind dasselbe Gehörte. */
function kern(text: string): string {
  return text.normalize("NFC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

/** `sofort` gefragt — ein Fehler darin kostet nur den Schnellpfad. */
function darfSofort(o: HoerOptionen, text: string, woerter: readonly SprachWort[] | undefined): boolean {
  try {
    return o.sofort ? o.sofort(text, woerter) === true : true;
  } catch {
    return false;
  }
}

/** `frueh` gefragt — ein Fehler darin kostet nur die Abkürzung, nie die Aufnahme. */
function istKlar(o: HoerOptionen, text: string, woerter: readonly SprachWort[] | undefined): boolean {
  try {
    return o.frueh?.(text, woerter) === true;
  } catch {
    return false;
  }
}

export class Zuhoerer {
  private lauf: DiktatLauf | null = null;
  private nr = 0;
  private uhren: Array<ReturnType<typeof setTimeout>> = [];
  private fertig: ((e: Pick<ZuhoerErgebnis, "grund" | "fehler">) => void) | null = null;

  constructor(
    private readonly anschluss: DiktatAnschluss,
    private readonly jetzt: () => number,
  ) {}

  get hoertZu(): boolean {
    return this.fertig !== null;
  }

  /**
   * Mikrofon auf und zuhören. ⚠️ `anschluss.starte` läuft SYNCHRON in diesem Aufruf (kein `await` davor): wer ihn aus dem Ende
   * des Tons ruft, stößt den Mikro-Start im selben Zug an — die AirPods schalten so früh um.
   */
  hoere(o: HoerOptionen = {}): Promise<ZuhoerErgebnis> {
    this.abbrechen();
    const nr = ++this.nr;
    const t0 = this.jetzt();
    let mikroMs: number | null = null;
    let text = "";
    let textKern = "";
    /** Das zuletzt NICHT leere Gehörte — falls die Erkennung es am Ende zurückzieht (`nachgeholt`). */
    let zuletzt: { text: string; woerter?: SprachWort[] } | null = null;
    let textBei: number | null = null;
    let frueh = false;
    /** Ist das Gehörte ganz bestätigt (nichts Vorläufiges) UND kann nicht mehr wachsen (`sofort`)? Nur mit `frueh` von Belang: dann ohne Abschluss. */
    let fest = false;
    /** Wann die Stille-Uhr abläuft (nach `jetzt`) — `null`: keine läuft. */
    let stilleBis: number | null = null;
    return new Promise<ZuhoerErgebnis>((aufloesen) => {
      const ende = (grund: ZuhoerGrund, fehler?: DiktatFehlerArt): void => {
        if (nr !== this.nr || !this.fertig) return;
        this.uhrenAus();
        this.fertig = null;
        const lauf = this.lauf;
        this.lauf = null;
        const zu = this.jetzt();
        const stilleMs = textBei === null ? null : Math.max(0, Math.round(zu - textBei));
        if (grund === "abbruch" || grund === "fehler" || !lauf) {
          try {
            lauf?.verwirf();
          } catch {
            /* schon zu */
          }
          aufloesen({ grund, text: "", mikroMs, fehler });
          return;
        }
        const istFrueh = frueh && grund === "stille";
        // Bestätigt und klar: nichts mehr abwarten — das Gehörte IST das Ergebnis, das Mikrofon geht ohne Abschluss zu.
        if (istFrueh && fest && zuletzt) {
          try {
            lauf.verwirf();
          } catch {
            /* schon zu */
          }
          aufloesen({ grund, text: zuletzt.text, woerter: zuletzt.woerter, mikroMs, stilleMs, abschlussMs: 0, frueh: true });
          return;
        }
        lauf.beende(istFrueh ? AUGEN_FRUEH_ABSCHLUSS_MS : undefined).then(
          (e) => {
            const leer = !kern(e.text) && !(e.woerter ?? []).some((w) => kern(w.text));
            const nachgeholt = leer && zuletzt !== null;
            const quelle = nachgeholt && zuletzt ? zuletzt : e;
            aufloesen({
              grund,
              text: quelle.text,
              woerter: quelle.woerter,
              mikroMs,
              stilleMs,
              abschlussMs: Math.max(0, Math.round(this.jetzt() - zu)),
              frueh: istFrueh,
              ...(nachgeholt ? { nachgeholt: true } : {}),
            });
          },
          () => aufloesen({ grund: "fehler", text: "", mikroMs, fehler: "sonst" }),
        );
      };
      this.fertig = (e) => ende(e.grund, e.fehler);
      const stille = (ms: number): void => {
        this.uhrenLoesche("stille");
        stilleBis = this.jetzt() + ms;
        this.uhr(ms, () => ende("stille"), "stille");
      };
      /** Die Stille-Uhr höchstens noch so lange — nie verlängern. */
      const stilleHoechstens = (ms: number): void => {
        if (stilleBis === null || stilleBis - this.jetzt() > ms) stille(ms);
      };
      let lauf: DiktatLauf;
      try {
        lauf = this.anschluss.starte({
          beiText: (ganz, woerter, festNeu) => {
            if (nr !== this.nr) return;
            text = ganz;
            const k = kern(ganz);
            if (k) zuletzt = { text: ganz, ...(woerter?.length ? { woerter: [...woerter] } : {}) };
            fest = festNeu === true && k !== "" && darfSofort(o, ganz, woerter);
            if (!k) return;
            // Dasselbe Gehörte noch einmal (bestätigt statt vorläufig, mit Punkt): die Stille läuft weiter — ist es jetzt
            // bestätigt und klar, höchstens noch `AUGEN_FEST_STILLE_MS`.
            if (k === textKern) {
              if (frueh && fest) stilleHoechstens(AUGEN_FEST_STILLE_MS);
              return;
            }
            textKern = k;
            textBei = this.jetzt();
            frueh = istKlar(o, ganz, woerter);
            stille(frueh ? (fest ? AUGEN_FEST_STILLE_MS : AUGEN_FRUEH_STILLE_MS) : AUGEN_STILLE_MS);
          },
          beiSatzende: () => {
            if (nr !== this.nr || !text.trim()) return;
            stille(frueh ? Math.min(SATZENDE_MS, fest ? AUGEN_FEST_STILLE_MS : AUGEN_FRUEH_STILLE_MS) : SATZENDE_MS);
          },
          beiFehler: (art) => ende("fehler", art),
        });
      } catch {
        this.lauf = null;
        aufloesen({ grund: "fehler", text: "", mikroMs: null, fehler: "sonst" });
        this.fertig = null;
        return;
      }
      // Meldete der Anschluss schon beim Start einen Fehler (synchron), ist die Aufnahme zu Ende, bevor wir `lauf` kennen: verwerfen.
      if (!this.fertig) {
        try {
          lauf.verwirf();
        } catch {
          /* schon zu */
        }
        return;
      }
      this.lauf = lauf;
      // Steht das Mikrofon nicht rechtzeitig, ist das ein Fehler — sonst wartete die Übung für immer.
      this.uhr(MIKRO_FRIST_MS, () => ende("fehler", "sonst"), "mikro");
      lauf.bereit.then(
        () => {
          if (nr !== this.nr || !this.fertig) return;
          mikroMs = Math.max(0, Math.round(this.jetzt() - t0));
          this.uhrenLoesche("mikro");
          this.uhr(AUGEN_WARTE_MS, () => (text.trim() ? undefined : ende("kein-text")), "kein-text");
          this.uhr(AUGEN_HOECHSTENS_MS, () => ende("hoechstens"), "hoechstens");
        },
        (e: unknown) => ende("fehler", e instanceof DiktatFehler ? e.art : "sonst"),
      );
    });
  }

  /** Alles aus, ohne etwas auszuwerten (Stopp, Pause, neue Aufnahme). Das offene Versprechen löst sich mit `abbruch`. */
  abbrechen(): void {
    const f = this.fertig;
    if (f) f({ grund: "abbruch" });
    this.uhrenAus();
  }

  private readonly namen: string[] = [];

  private uhr(ms: number, f: () => void, name: string): void {
    this.uhren.push(setTimeout(f, ms));
    this.namen.push(name);
  }

  private uhrenLoesche(name: string): void {
    for (let i = this.namen.length - 1; i >= 0; i--) {
      if (this.namen[i] !== name) continue;
      clearTimeout(this.uhren[i]);
      this.uhren.splice(i, 1);
      this.namen.splice(i, 1);
    }
  }

  private uhrenAus(): void {
    for (const u of this.uhren) clearTimeout(u);
    this.uhren = [];
    this.namen.length = 0;
  }
}
